import { createHash } from 'node:crypto';
import { z } from 'zod';
import { env } from '@/config/env';
import { getRecommendedArchicadPluginVersion } from '@/lib/archicad/plugin-version';
import {
  ARCHICAD_WINDOWS_INSTALLER_DOWNLOAD,
  PLUGIN_DOWNLOADS,
  type PluginDownload,
} from '@/lib/downloads/config';
import { parsePluginVersion } from '@/lib/host-bridge/plugin-version';
import { getRecommendedPluginVersion } from '@/lib/revit/plugin-version';
import { getRecommendedRhinoPluginVersion } from '@/lib/rhino/plugin-version';

/**
 * Plugin update manifest
 *
 * Server-driven release metadata for the desktop plugins (connectors).
 * Served by GET /api/plugin-updates so update banners and plugin-side
 * updaters can discover the latest release without a client rebuild.
 *
 * `lib/downloads/config.ts` stays the authoring source for artifacts;
 * this module derives the manifest from it plus server-side env
 * overrides for hashes, minimum versions, and the auto-update
 * kill-switch.
 */

export const pluginUpdateConnectorSchema = z.enum([
  'revit',
  'archicad',
  'rhino',
]);

export type PluginUpdateConnector = z.infer<typeof pluginUpdateConnectorSchema>;

export const pluginUpdateEntrySchema = z.object({
  /** Stable artifact id, matches PluginDownload.id */
  id: z.string(),
  connector: pluginUpdateConnectorSchema,
  platform: z.enum(['windows', 'macos']),
  /** Host application versions this artifact supports (e.g. Revit years, Archicad majors) */
  hostVersions: z.array(z.string()),
  /**
   * Latest downloadable plugin version, and the only version field the
   * update agents read: they compare it against the running version to
   * decide whether to stage an update. Moving it is what advances (or
   * rolls back) a rollout.
   */
  latestVersion: z.string(),
  /**
   * Version below which the app nudges users to update. App-side only —
   * it drives the in-app compat banner and nothing else. No update agent
   * consumes it (Revit's UpdateManifest.cs does not even deserialize it),
   * so changing it cannot start, stop, or steer an auto-update.
   */
  recommendedVersion: z.string(),
  /** Version below which the plugin is considered incompatible; null = no hard floor */
  minVersion: z.string().nullable(),
  downloadUrl: z.string().nullable(),
  filename: z.string(),
  /** Hex-encoded SHA-256 of the artifact; null until release CI publishes hashes */
  sha256: z.string().nullable(),
  /**
   * Raw install-folder zip the in-plugin update agent stages (content at
   * the zip root — the swapper swaps the extracted root in as the install
   * dir). Null until release CI/ops publish it; agents stay dormant.
   */
  payloadUrl: z.string().nullable(),
  payloadSha256: z.string().nullable(),
  /**
   * The arkyv-updater swapper artifact for this entry: a zip for revit
   * (its agent extracts), a raw executable for archicad (no zip handling
   * in the add-on). Rhino bundles the swapper at build time and leaves
   * these null.
   */
  swapperUrl: z.string().nullable(),
  swapperSha256: z.string().nullable(),
  /** Kill-switch: when false, plugin-side updaters must not auto-install this artifact */
  autoUpdate: z.boolean(),
  /**
   * When false — the default — the swapper skips its client-side hold-down
   * bookkeeping (the `unproven` counter and the resulting cooldown) and keeps
   * applying staged versions. Opt in per plugin/platform once the fleet is
   * large enough to want the brake. Server-side verdicts — the `autoUpdate`
   * kill switch and re-pointing `latestVersion` — remain the authoritative
   * brake either way (ENG-1257).
   */
  shouldHoldDown: z.boolean(),
});

export type PluginUpdateEntry = z.infer<typeof pluginUpdateEntrySchema>;

export const pluginUpdateManifestSchema = z.object({
  entries: z.array(pluginUpdateEntrySchema),
});

export type PluginUpdateManifest = z.infer<typeof pluginUpdateManifestSchema>;

const SHA256_PATTERN = /^[0-9a-f]{64}$/i;

function getArtifactSha256(id: PluginDownload['id']): string | null {
  const envKey = `PLUGIN_UPDATE_SHA256_${id.replace(/-/g, '_').toUpperCase()}`;
  const configured = process.env[envKey]?.trim();
  if (configured && SHA256_PATTERN.test(configured)) {
    return configured.toLowerCase();
  }

  return null;
}

function getUpdaterEnvValue(suffix: string, artifactId: string): string | null {
  const envKey = `PLUGIN_UPDATE_${suffix}_${artifactId.replace(/-/g, '_').toUpperCase()}`;
  return process.env[envKey]?.trim() || null;
}

function getUpdaterUrl(suffix: string, artifactId: string): string | null {
  const configured = getUpdaterEnvValue(suffix, artifactId);
  return configured?.startsWith('https://') ? configured : null;
}

function getUpdaterSha256(suffix: string, artifactId: string): string | null {
  const configured = getUpdaterEnvValue(suffix, artifactId);
  if (configured && SHA256_PATTERN.test(configured)) {
    return configured.toLowerCase();
  }
  return null;
}

/**
 * The auto-update artifact fields for one manifest entry, env-driven per
 * artifact id (same convention as PLUGIN_UPDATE_SHA256_<ID>): e.g.
 * PLUGIN_UPDATE_PAYLOAD_URL_REVIT_PLUGIN. All null until ops publish the
 * artifacts — the agents treat missing fields as "stay dormant".
 */
function getUpdaterFields(
  artifactId: string,
): Pick<
  PluginUpdateEntry,
  'payloadUrl' | 'payloadSha256' | 'swapperUrl' | 'swapperSha256'
> {
  return {
    payloadUrl: getUpdaterUrl('PAYLOAD_URL', artifactId),
    payloadSha256: getUpdaterSha256('PAYLOAD_SHA256', artifactId),
    swapperUrl: getUpdaterUrl('SWAPPER_URL', artifactId),
    swapperSha256: getUpdaterSha256('SWAPPER_SHA256', artifactId),
  };
}

/**
 * Staged-rollout gate (ENG-1249): a percent per artifact id (e.g.
 * PLUGIN_UPDATE_ROLLOUT_PERCENT_REVIT_PLUGIN) plus the caller's
 * anonymous machine id decide whether this response serves the armed
 * updater fields or the dormant nulls. Deterministic hash bucketing —
 * the same machine always lands in the same cohort, so widening the
 * percent only ever adds machines. Missing env = 100 (no staged rollout
 * in play); an unparseable value fails safe to 0 (dormant beats
 * accidentally arming the fleet on a typo).
 */
function getRolloutPercent(artifactId: string): number {
  const configured = getUpdaterEnvValue('ROLLOUT_PERCENT', artifactId);
  if (configured === null) {
    return 100;
  }
  const percent = Number.parseInt(configured, 10);
  if (Number.isNaN(percent) || percent < 0 || percent > 100) {
    return 0;
  }
  return percent;
}

/**
 * The client id arrives as an untrusted query parameter; it must never
 * be used for anything beyond cohort bucketing. Lying about it lets a
 * machine opt itself in or out of a rollout wave — harmless, since the
 * artifacts are public, signed, and hash-pinned.
 */
export function normalizeRolloutClientId(raw: string | null): string | null {
  const trimmed = raw?.trim().toLowerCase() ?? '';
  return /^[0-9a-f-]{8,64}$/.test(trimmed) ? trimmed : null;
}

function isInRolloutCohort(
  artifactId: string,
  clientId: string | null,
): boolean {
  const percent = getRolloutPercent(artifactId);
  if (percent >= 100) {
    return true;
  }
  if (percent <= 0 || clientId === null) {
    // Agents without a machine id (pre-ENG-1249 builds) cannot be
    // bucketed stably; during a staged window they wait for 100%.
    return false;
  }
  const digest = createHash('sha256')
    .update(`${clientId}:${artifactId}`)
    .digest();
  return digest.readUInt32BE(0) % 100 < percent;
}

/**
 * Nulls exactly the four updater fields for out-of-cohort machines —
 * the agents' existing dormancy semantics. Everything else on the entry
 * (recommendedVersion, minVersion, download links) always flows: the
 * gate must never leak into the compat-banner mechanism.
 */
function gateUpdaterFields(
  artifactId: string,
  clientId: string | null,
  fields: ReturnType<typeof getUpdaterFields>,
): ReturnType<typeof getUpdaterFields> {
  if (fields.payloadUrl === null && fields.swapperUrl === null) {
    return fields;
  }
  if (isInRolloutCohort(artifactId, clientId)) {
    return fields;
  }
  return {
    payloadUrl: null,
    payloadSha256: null,
    swapperUrl: null,
    swapperSha256: null,
  };
}

/**
 * Compatibility floors that hold without configuration. Only Revit has one: a
 * long-standing floor that predates the update manifest. Rhino's kernel-cutover
 * wall is deliberately left env-only so it can be dropped in seconds if the
 * cutover misbehaves — a code default could only be lowered by a deploy. Archicad
 * has no floor, because one would wall existing users out of the product
 * entirely rather than just nudging them to update.
 */
const DEFAULT_CONNECTOR_MIN_VERSION: Partial<
  Record<PluginUpdateConnector, string>
> = {
  revit: '1.1.0',
};

function getConnectorMinVersion(
  connector: PluginUpdateConnector,
): string | null {
  const envKey = `PLUGIN_UPDATE_MIN_VERSION_${connector.toUpperCase()}`;
  const configured = process.env[envKey]?.trim();
  if (configured && parsePluginVersion(configured)) {
    return configured;
  }

  return DEFAULT_CONNECTOR_MIN_VERSION[connector] ?? null;
}

function getDisabledConnectors(): Set<string> {
  const configured = process.env.PLUGIN_AUTO_UPDATE_DISABLED?.trim();
  if (!configured) {
    return new Set();
  }

  const entries = configured
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean);

  if (entries.includes('all')) {
    return new Set(pluginUpdateConnectorSchema.options);
  }

  return new Set(entries);
}

/**
 * Read lazily rather than at module load: this module is also imported by
 * client components (e.g. `hooks/use-plugin-update-manifest.ts`) for the shared
 * schema and helpers, and the `@t3-oss/env-nextjs` proxy throws when
 * server-only variables are touched in the browser. Everything below is only
 * reached from {@link buildPluginUpdateManifest}, which runs server-side.
 */
function getHoldDownFlags(): Record<string, string | undefined> {
  return {
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED: env.PLUGIN_UPDATE_HOLD_DOWN_ENABLED,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_REVIT:
      env.PLUGIN_UPDATE_HOLD_DOWN_ENABLED_REVIT,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_REVIT_WINDOWS:
      env.PLUGIN_UPDATE_HOLD_DOWN_ENABLED_REVIT_WINDOWS,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_REVIT_MACOS:
      env.PLUGIN_UPDATE_HOLD_DOWN_ENABLED_REVIT_MACOS,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_RHINO:
      env.PLUGIN_UPDATE_HOLD_DOWN_ENABLED_RHINO,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_RHINO_WINDOWS:
      env.PLUGIN_UPDATE_HOLD_DOWN_ENABLED_RHINO_WINDOWS,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_RHINO_MACOS:
      env.PLUGIN_UPDATE_HOLD_DOWN_ENABLED_RHINO_MACOS,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_ARCHICAD:
      env.PLUGIN_UPDATE_HOLD_DOWN_ENABLED_ARCHICAD,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_ARCHICAD_WINDOWS:
      env.PLUGIN_UPDATE_HOLD_DOWN_ENABLED_ARCHICAD_WINDOWS,
    PLUGIN_UPDATE_HOLD_DOWN_ENABLED_ARCHICAD_MACOS:
      env.PLUGIN_UPDATE_HOLD_DOWN_ENABLED_ARCHICAD_MACOS,
  };
}

function isFlagEnabled(value: string | undefined): boolean {
  return value === 'true' || value === '1' || value === 'yes';
}

/**
 * Whether the client-side hold-down is switched ON for this artifact. Opt-in:
 * with nothing configured this is false. Most-specific-first: connector+platform
 * (e.g. Archicad on macOS alone), then the whole connector, then the global
 * switch. See the `shouldHoldDown` field on {@link pluginUpdateEntrySchema}
 * (ENG-1257).
 */
function isHoldDownEnabled(
  connector: PluginUpdateConnector,
  platform: 'windows' | 'macos',
): boolean {
  const upperConnector = connector.toUpperCase();
  const keys = [
    `PLUGIN_UPDATE_HOLD_DOWN_ENABLED_${upperConnector}_${platform.toUpperCase()}`,
    `PLUGIN_UPDATE_HOLD_DOWN_ENABLED_${upperConnector}`,
    'PLUGIN_UPDATE_HOLD_DOWN_ENABLED',
  ];

  // First *configured* key wins, so a specific `false` switches the brake back
  // off under a broader `true` (e.g. enabled globally, one connector held out).
  const flags = getHoldDownFlags();
  for (const key of keys) {
    const configured = flags[key];
    if (configured !== undefined) {
      return isFlagEnabled(configured);
    }
  }

  return false;
}

function getConnectorRecommendedVersionForBuild(
  connector: PluginUpdateConnector,
): string {
  switch (connector) {
    case 'revit':
      return getRecommendedPluginVersion();
    case 'rhino':
      return getRecommendedRhinoPluginVersion();
    case 'archicad':
      return getRecommendedArchicadPluginVersion();
  }
}

export function buildPluginUpdateManifest(
  rolloutClientId: string | null = null,
): PluginUpdateManifest {
  const disabledConnectors = getDisabledConnectors();

  const installerEntries: PluginUpdateEntry[] = PLUGIN_DOWNLOADS.map(
    (download) => {
      const connector = download.product;

      // The unified Revit installer entry carries no platform in the
      // downloads config; Revit is Windows-only.
      const platform = download.platform ?? 'windows';

      return {
        id: download.id,
        connector,
        platform,
        hostVersions: download.versions,
        latestVersion: download.version,
        recommendedVersion: getConnectorRecommendedVersionForBuild(connector),
        minVersion: getConnectorMinVersion(connector),
        downloadUrl: download.url,
        filename: download.filename,
        sha256: getArtifactSha256(download.id),
        ...gateUpdaterFields(
          download.id,
          rolloutClientId,
          getUpdaterFields(download.id),
        ),
        autoUpdate: !disabledConnectors.has(connector),
        shouldHoldDown: isHoldDownEnabled(connector, platform),
      };
    },
  );

  // The synthetic per-version archicad entries go first so that an agent
  // matching on connector/platform/hostVersions can never resolve the
  // combined installer entry (which shares those host versions) ahead of
  // its armed per-version entry.
  return {
    entries: [
      ...buildArchicadWindowsUpdaterEntries(
        disabledConnectors,
        rolloutClientId,
      ),
      ...installerEntries,
    ],
  };
}

/**
 * The archicad Windows installer is one combined artifact for AC 27/28/29,
 * but the in-plugin update agent needs a per-AC-version payload (it matches
 * entries whose hostVersions contain its compiled AC version). These
 * synthetic entries exist only when their payload/swapper env values are
 * fully configured; the downloads page matches by the known installer ids
 * and ignores them.
 */
function buildArchicadWindowsUpdaterEntries(
  disabledConnectors: Set<string>,
  rolloutClientId: string | null,
): PluginUpdateEntry[] {
  const base = ARCHICAD_WINDOWS_INSTALLER_DOWNLOAD;

  return base.versions.flatMap((hostVersion) => {
    const id = `archicad-windows-${hostVersion}`;
    const updaterFields = getUpdaterFields(id);
    if (
      !updaterFields.payloadUrl ||
      !updaterFields.payloadSha256 ||
      !updaterFields.swapperUrl ||
      !updaterFields.swapperSha256
    ) {
      return [];
    }

    return [
      {
        id,
        connector: 'archicad' as const,
        platform: 'windows' as const,
        hostVersions: [hostVersion],
        latestVersion: base.version,
        recommendedVersion: getConnectorRecommendedVersionForBuild('archicad'),
        minVersion: getConnectorMinVersion('archicad'),
        downloadUrl: base.url,
        filename: base.filename,
        sha256: getArtifactSha256(base.id),
        // The entry stays present for out-of-cohort machines (dormant
        // nulls) so its recommended/min versions keep flowing.
        ...gateUpdaterFields(id, rolloutClientId, updaterFields),
        autoUpdate: !disabledConnectors.has('archicad'),
        shouldHoldDown: isHoldDownEnabled('archicad', 'windows'),
      },
    ];
  });
}

/**
 * Recommended version for a connector from a fetched manifest, or null
 * when the manifest is unavailable (callers fall back to the build-time
 * constants).
 */
export function getConnectorRecommendedVersion(
  manifest: PluginUpdateManifest | undefined,
  connector: PluginUpdateConnector,
): string | null {
  const entry = manifest?.entries.find(
    (candidate) => candidate.connector === connector,
  );

  return entry?.recommendedVersion ?? null;
}
